/**
 * Demo Data Service
 *
 * Replaces Firebase for recruiter / demo purposes.
 * Patient verification checks against a single built-in demo patient.
 * Final submission saves data to sessionStorage (cleared after submit).
 *
 * Demo credentials:
 *   First Name : Jane
 *   Last Name  : Demo
 *   Date of Birth: May 15, 1990
 */

export const DEMO_PATIENT_ID = "demo-patient-001";
export const DEMO_ENCOUNTER_ID = "ENC-DEMO-001";

/** Credentials shown on the EncounterVerification page for recruiters */
export const DEMO_CREDENTIALS = {
  firstName: "Jane",
  lastName: "Demo",
  dateOfBirth: "1990-05-15", // YYYY-MM-DD
  displayDOB: "May 15, 1990",
};

const getTodayFormatted = () =>
  new Date().toLocaleDateString("en-us", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

export const getDemoPatient = () => ({
  id: DEMO_PATIENT_ID,
  firstName: "Jane",
  lastName: "Demo",
  middleName: "",
  firstNameLower: "jane",
  lastNameLower: "demo",
  dateOfBirth: "1990-05-15",
  gender: "Female",
  checkInStatus: "not-checked-in",
  encounterId: DEMO_ENCOUNTER_ID,
  appointmentDate: getTodayFormatted(), // always today
  appointmentStartTime: "10:30 AM",
  appointmentProviderName: "Dr. Alyssa Pham",
  facilityName: "KATY",
  address: {
    street: "4521 Oak Ridge Drive",
    street2: "",
    city: "Katy",
    state: "TX",
    zipCode: "77494",
  },
  phone: "832-555-0174",
  email: "jane.demo@example.com",
  insurance: {
    provider: "Blue Cross Blue Shield",
    policyNumber: "BCBS-4521890",
    groupName: "Employer Group Plan",
    groupNumber: "GRP-7720",
    phoneNumber: "1-800-262-2583",
    effectiveDate: "01/01/2024",
    copay: "40",
    specialistCopay: "100",
  },
});

/**
 * Verify demo patient by name and DOB.
 * Accepts the demo credentials only; returns null for anything else.
 */
export const verifyPatientByNameAndDOB = async (
  firstName,
  lastName,
  dateOfBirth
) => {
  const demo = getDemoPatient();
  const firstMatch =
    firstName.toLowerCase().trim() === demo.firstNameLower;
  const lastMatch =
    lastName.toLowerCase().trim() === demo.lastNameLower;
  const dobMatch = dateOfBirth === demo.dateOfBirth;

  if (firstMatch && lastMatch && dobMatch) {
    return demo;
  }
  return null;
};

/**
 * Simulate submitting kiosk check-in data.
 * Saves the full submission to sessionStorage so nothing is lost,
 * returns a fake submission ID (mirrors the Firestore document ID).
 */
export const submitKioskData = async (patientId, kioskData) => {
  const submissionId = `demo-${Date.now()}`;
  const submission = {
    submissionId,
    patientId,
    kioskData,
    checkInTime: new Date().toISOString(),
  };
  sessionStorage.setItem("demoKioskSubmission", JSON.stringify(submission));
  console.log(
    "✅ [DEMO] Check-in data saved to sessionStorage:",
    submissionId
  );
  return submissionId;
};
